import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'spotlight-ui/environments/environment';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DebugLoggerService } from '../core/debug-logger.service';
import { ErrorTrackingService } from '@spotlight/shared/error-tracking';

/**
 * Adds trace headers to requests sent to our own APIs so a request can be found
 * in Datadog APM by its trace ID.
 *
 * Sends W3C `traceparent` plus the equivalent `x-datadog-*` headers. The Datadog
 * trace ID is the low 64 bits of the W3C trace ID, as a decimal string, so
 * search Datadog with `trace_id:<decimal>`. The same ID is added to the request's Bugsnag
 * breadcrumb as `datadogTraceId`.
 */
@Injectable()
export class TraceIdInterceptor implements HttpInterceptor {
  constructor(
    private readonly logger: DebugLoggerService,
    private readonly errorTracking: ErrorTrackingService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const origins = [...(environment.urlsForAuthHeader ?? []), ...(environment.urlsForJWTHeader ?? [])];
    if (!origins.some(url => request.urlWithParams.includes(url))) {
      return next.handle(request);
    }

    const traceIdLow = TraceIdInterceptor.randomId64();
    const traceIdHigh = TraceIdInterceptor.randomId64();
    const spanId = TraceIdInterceptor.randomId64();
    const hex = (id: bigint) => id.toString(16).padStart(16, '0');
    const datadogTraceId = traceIdLow.toString();
    this.errorTracking.recordRequestTraceId(request.method, request.urlWithParams, datadogTraceId);

    const traced = request.clone({
      setHeaders: {
        traceparent: `00-${hex(traceIdHigh)}${hex(traceIdLow)}-${hex(spanId)}-01`,
        'x-datadog-trace-id': datadogTraceId,
        'x-datadog-parent-id': spanId.toString(),
        'x-datadog-sampling-priority': '1',
      }
    });

    const label = `${request.method} ${request.url} Datadog trace_id:${datadogTraceId} (hex ${hex(traceIdLow)})`;
    this.logger.info(`[Trace] ${label}`);

    return next.handle(traced).pipe(
      tap({ error: error => this.logger.error(`[Trace] failed: ${label}`, error?.status) })
    );
  }

  /** Random non-zero unsigned 64-bit integer. */
  private static randomId64(): bigint {
    const [hi, lo] = crypto.getRandomValues(new Uint32Array(2));
    const id = (BigInt(hi) << 32n) | BigInt(lo);
    return id === 0n ? 1n : id;
  }
}
